import * as React from 'react';
import {FunctionComponent, useMemo} from 'react';
import {PlainGraph} from '../../../Graphs/PlainGraph';
import {formatDuration} from '../../Support/Formatting/formatDuration';
import {Duration} from '../../../Domain/Duration';
import {HeartRatePoint, HeartRateTrack} from '../../../Models/HeartRateTrack';
import {formatHeartRate} from '../../Support/Formatting/formatHeartRate';
import {HeartRate} from '../../../Domain/HeartRate';

interface OwnProps {
    heartRateTrack: HeartRateTrack;
    tooltipAt: {x: number; elapsedTime: Duration} | null;
    range: {min: Duration; max: Duration};
    onTooltipActivated: (point: {x: number; elapsedTime: Duration}) => void;
}

export const OutdoorsHeartRateGraph: FunctionComponent<OwnProps> = ({
    heartRateTrack,
    range,
    tooltipAt,
    onTooltipActivated,
}) => {
    if (heartRateTrack.length === 0) {
        return null;
    }

    const points = heartRateTrack;

    const minBpmBuffer = 20;
    const maxBpmBuffer = 20;
    const minBpm = Math.max(
        0,
        Math.min.apply(
            0,
            points.map((point) => point.heart_rate.bpm),
        ) - minBpmBuffer,
    );
    const maxBpm = Math.min(
        Math.max.apply(
            0,
            points.map((point) => point.heart_rate.bpm),
        ) + maxBpmBuffer,
    );

    const axis = useMemo(() => {
        const axisData: {
            x: Array<{
                value: number;
                label: string;
            }>;
            y: Array<{
                value: number;
                label: string;
            }>;
        } = {x: [], y: []};

        const minX = range.min.milliseconds;
        const maxX = range.max.milliseconds;
        const xStep = (maxX - minX) / 8;

        for (let i = 0; i < 8; i++) {
            const valueX = minX + xStep * i;
            axisData.x.push({
                value: minX + xStep * i,
                label: formatDuration(Duration.fromMilliseconds(valueX)).formatted,
            });
        }

        for (let i = 0; i < 4; i++) {
            const bpm = minBpm + (maxBpm - minBpm) * (i / 4);
            axisData.y.push({
                value: i / 4,
                label: formatHeartRate(HeartRate.fromBpm(bpm)).formatted,
            });
        }

        return axisData;
    }, [heartRateTrack]);

    const values = useMemo(() => {
        return points
            .map((point) => {
                return {
                    x: point.elapsed_time.milliseconds,
                    y: (point.heart_rate.bpm - minBpm) / (maxBpm - minBpm), //Normalize to 0-1 between min and max bpm
                };
            })
            .map((value) => {
                return {
                    x: value.x,
                    y: value.y < 0 ? 0 : value.y > 1 ? 1 : value.y,
                };
            });
    }, [heartRateTrack]);

    const tooltipGeoPointWithSpeed =
        tooltipAt === null
            ? null
            : points.reduce((closestPoint: HeartRatePoint | null, point) => {
                  if (closestPoint === null) {
                      return point;
                  }

                  //If the elapsed time of the point is closer to the elapsed time of the tooltip, use that point
                  if (
                      Math.abs(point.elapsed_time.milliseconds - tooltipAt.elapsedTime.milliseconds) <
                      Math.abs(closestPoint.elapsed_time.milliseconds - tooltipAt.elapsedTime.milliseconds)
                  ) {
                      return point;
                  }
                  return closestPoint;
              }, null);

    return (
        <PlainGraph
            color="#DC3545"
            height={80}
            padding={{top: 0, right: 0, bottom: 20, left: 20}}
            axis={axis}
            values={values}
            tooltip={
                tooltipGeoPointWithSpeed &&
                tooltipAt && {
                    x: tooltipAt.x,
                    content: [
                        'bpm: ' + formatHeartRate(tooltipGeoPointWithSpeed.heart_rate ?? null).formatted,
                        'Time: ' + formatDuration(tooltipGeoPointWithSpeed?.elapsed_time ?? null).formatted,
                    ].join('\n'),
                }
            }
            onDataActivated={(data) => {
                onTooltipActivated({
                    x: data.x,
                    elapsedTime: Duration.fromMilliseconds(data.point.x),
                });
            }}
        />
    );
};
